import React, { useContext, useEffect, useState } from "react";
import images from "../../../assets/images/constantImages";
import Overview from "./Overview";
import Security from "./Security";
import BillingPlans from "./BillingPlans";
import SubscriptionPlan from "./SubscriptionPlan";
import apiService, { handleError } from "../../Common/ApiCommon/ApiService";
import icons from "../../../assets/images/constantIcons";
import { Form, Formik } from "formik";
import toast from "react-hot-toast";
import { UserContext } from "../../Layout/UserContext";
import { useFormik } from "formik";
import * as Yup from "yup";

const AccountSetting = () => {
  const [activeTab, setActiveTab] = useState("Overview");
  const [profileUpdate, setProfileUpdate] = useState(false);
  const [image, setImage] = useState(null);
  const { userData, updateUserData } = useContext(UserContext);
  const [editMode, setEditMode] = useState(false);
  const [submit, setSubmit] = useState(false);
  const [previewImage, setPreviewImage] = useState(null);
  const userId = userData?.userId;

  const getInitials = (fullName) => {
    if (!fullName) return "";
    const nameParts = fullName.trim().split(" ");
    if (nameParts.length === 1) return nameParts[0][0].toUpperCase();
    return (nameParts[0][0] + nameParts[1][0]).toUpperCase();
  };

  const fetchVendor = async () => {
    try {
      const response = await apiService.get(`/vendors/${userId}`);
      updateUserData({
        ...userData,
        first_name: response.data.first_name,
        store_name: response.data.store_name,
        phone_no: response.data.phone_no,
        street_address: response.data.street_address,
        store_logo: response.data.store_logo,
        description: response.data.description,
        email: response.data.email,
        image: response.data.image,
      });

      setPreviewImage(
        response.data.store_logo
          ? `https://headquater.pixoatic.com${response.data.store_logo}`
          : images.defaultprofilepicture
      );
    } catch (errors) {
      handleError(errors);
    }
  };

  useEffect(() => {
    fetchVendor();
  }, [userId]);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      first_name: userData.first_name || "",
      store_name: userData.store_name || "",
      phone_no: userData.phone_no || "",
      street_address: userData.street_address || "",
      store_logo: userData.store_logo || "",
      description: userData.description || "",
      email: userData.email || "",
    },
    validationSchema: Yup.object({
      first_name: Yup.string().required("Name is required"),
      store_name: Yup.string().required("Store Name is required"),
    }),
    onSubmit: async (values) => {
      setSubmit(true);
      try {
        const formData = new FormData();
        formData.append("vendor[first_name]", values.first_name);
        formData.append("vendor[store_name]", values.store_name);
        formData.append("vendor[phone_no]", values.phone_no);
        formData.append("vendor[street_address]", values.street_address);
        formData.append("vendor[email]", values.email);

        formData.append("vendor[description]", values.description);
        if (values.store_logo instanceof File) {
          formData.append("vendor[store_logo]", values.store_logo);
        }

        const response = await apiService.put(`/vendors/${userId}`, formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });

        toast.success(response.message, { duration: 2000 });
        setEditMode(false);
        fetchVendor();

        updateUserData({
          ...userData,
          email: values.email,
          first_name: values.first_name,
          store_name: values.store_name,
          phone_no: values.phone_no,
          street_address: values.street_address,
          store_logo:
            values.store_logo instanceof File
              ? URL.createObjectURL(values.store_logo)
              : userData.store_logo,
          description: values.description,
          image: response.data.image,
        });
      } catch (error) {
        handleError(error);
      } finally {
        setSubmit(false);
      }
    },
  });

  const handleFileChange = (event) => {
    const file = event.currentTarget.files[0];
    if (file) {
      formik.setFieldValue("store_logo", file);
      setPreviewImage(URL.createObjectURL(file));
    }
  };

  const handleImageChange = (e) => {
    setImage(e.target.files[0]);
  };

  const handleUpdateProfileImage = async () => {
    try {
      if (!image) return;

      const formData = new FormData();
      formData.append("vendor[image]", image);

      const response = await apiService.put(
        `/vendors/${userData.userId}`,
        formData
      );

      updateUserData((prev) => ({
        ...prev,
        image: response.data.image,
      }));
      fetchVendor()
      toast.success("Profile image updated successfully!");
      setProfileUpdate(false);
    } catch (errors) {
      handleError(errors);
    }
  };

  const renderContent = () => {
    switch (activeTab) {
      case "Overview":
        return <Overview />;
      case "Security":
        return <Security />;
      case "BillingPlans":
        return <BillingPlans />;
      case "SubscriptionPlan":
        return <SubscriptionPlan />;
      default:
        return null;
    }
  };

  const Store_logo = userData?.store_logo
    ? `https://headquater.pixoatic.com${userData.store_logo}`
    : "";

  const fullImageUrl =
    userData.userType === "vendor" && userData.image
      ? `https://headquater.pixoatic.com${userData.image}`
      : images.defaultprofilepicture;

  const profileInfo = [
    { label: "Contact", value: userData?.phone_no },
    { label: "Email", value: userData?.email },
    { label: "Address", value: userData?.street_address || "N/A"},
    { label: "Description", value: userData?.description || "N/A" },
    { label: "Store Name", value: userData?.store_name },
    {
      label: "Store Logo",
      value: (
        <span className="flex items-center font-normal">
          {Store_logo ? (
            <img
              src={Store_logo}
              alt="Store Logo"
              loading="lazy"
              className="w-14 h-14 rounded-full object-fill border border-G20"
            />
          ) : (
            <span className="text-sm text-gray-500" onClick={() => setEditMode(true)}>Upload</span>
          )}
        </span>
      ),
      isCustom: true,
    },
  ];

  return (
    <div className="flex flex-col w-full">
      {/* <div className="m-6 mx-6"> */}
      <div className="mx-4 md:mx-6 my-6">
        <h1 className="main_heading text-2xl xl:text-[30px] mb-5">Setting</h1>
        <div className="bg-white rounded-2xl shadow-md">
          <div className="flex flex-col md:flex-row justify-between items-start p-6 gap-6 md:gap-4 ">
            <div className="flex flex-col md:flex-row items-start  gap-4">
              <div className="relative">
                {userData?.image && userData.image !== "undefined" ? (
                  <img
                    src={`https://headquater.pixoatic.com${userData.image}`}
                    alt="Profile"
                    loading="lazy"
                    onError={(e) => {
                      e.target.onerror = null;
                      updateUserData((prev) => ({ ...prev, image: null }));
                    }}
                    className="w-[100px] h-[100px] object-cover rounded-full border-2 border-white shadow-xl mb-4"
                  />
                ) : (
                  <div className="w-[100px] h-[100px] flex items-center justify-center bg-blueButton text-white text-2xl font-semibold rounded-full border-2 border-white shadow-xl mb-4">
                    {getInitials(userData?.first_name)}
                  </div>
                )}
                <div
                  onClick={() => setProfileUpdate(true)}
                  className="flex justify-center items-center w-9 h-9 p-1 bg-green50 rounded-full absolute bottom-5 right-0 border border-white cursor-pointer"
                >
                  <icons.TbEdit className="text-green text-2xl" />
                </div>
              </div>
              <div className="flex flex-col items-start gap-2">
                <h1 className="text-G800 text-lg md:text-xl font-semibold">
                  {userData?.first_name}
                </h1>

                <div className="grid grid-cols-1 xs:grid-cols-2 gap-1 md:gap-2 w-full">
                  {profileInfo.map((item, index) => (
                    <div
                      key={index}
                      className={`flex  gap-2 text-xs sm:text-sm text-G500`}
                    >
                      <p className="font-medium">{item.label}:</p>
                      {item.isCustom ? (
                        item.value
                      ) : (
                        <p className={`font-normal ${item.className || ""}`}>
                          {item.value}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
            <div className="">
              <button
                className="bg-purple bg-opacity-10 text-purple hover:text-white hover:bg-gray-600 font-medium md:px-6 py-[6px] px-3 text-sm xl:text-base rounded-lg"
                onClick={() => setEditMode(true)}
              >
                Edit
              </button>
            </div>
          </div>

          {/* Profile Image Update Modal */}
          {profileUpdate && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
              <div className="bg-white rounded-lg p-6 w-[350px] md:w-[460px] relative">
                <div className="flex justify-between items-center mb-4">
                  <h2 className="text-lg font-semibold text-G500">
                    Update Profile Image
                  </h2>
                  <div
                    className="absolute top-7 right-3 bg-green50 rounded-full p-2 cursor-pointer w-[32px] h-[32px]"
                    onClick={() => setProfileUpdate(false)}
                  >
                    <icons.IoClose className="text-green700" />
                  </div>
                </div>
                <Formik
                  initialValues={{ image: null }}
                  onSubmit={handleUpdateProfileImage}
                >
                  {({ isSubmitting }) => (
                    <Form className="flex flex-col">
                      <div className="flex flex-col items-center">
                        <img
                          src={
                            image ? URL.createObjectURL(image) : fullImageUrl
                          }
                          alt="Preview"
                          loading="lazy"
                          className="w-32 h-32 object-cover rounded-full mb-2 border"
                        />
                      </div>
                      <label className="cursor-pointer text-G300 text-sm mb-2">
                        Upload Image
                      </label>
                      <input
                        type="file"
                        accept="image/*"
                        className="text-sm inputtext"
                        onChange={handleImageChange}
                      />
                      <div className="flex justify-end space-x-4 pt-4">
                        <button
                          type="button"
                          onClick={() => setProfileUpdate(false)}
                          className="w-[93px] text-sm py-2 border border-green text-green rounded-lg"
                        >
                          Close
                        </button>
                        <button
                          type="submit"
                          className={`w-[93px] text-sm py-2 rounded-lg ${isSubmitting
                            ? "bg-green50 cursor-not-allowed"
                            : "bg-green text-white"
                            }`}
                          disabled={isSubmitting}
                        >
                          {isSubmitting ? "Updating..." : "Update"}
                        </button>
                      </div>
                    </Form>
                  )}
                </Formik>
              </div>
            </div>
          )}

          {/* Edit Vendor Details Modal */}
          {editMode && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
              <div className="bg-white rounded-lg p-6 w-[350px] md:w-[460px] relative">
                <div className="flex justify-between items-center mb-4">
                  <h2 className="text-lg font-semibold">Edit Details</h2>
                  <div
                    className="absolute flex justify-center items-center right-3 bg-green50 rounded-full p-2 cursor-pointer w-[32px] h-[32px]"
                    onClick={() => setEditMode(false)}
                  >
                    <icons.IoClose className="text-green700 " />
                  </div>
                </div>

                <form
                  onSubmit={formik.handleSubmit}
                  className="space-y-4 h-[60vh] sm:h-[70vh] overflow-y-auto vertical"
                >
                  <div className="text-G300">
                    <label className="block text-sm font-normal mb-2">
                      Name
                    </label>
                    <input
                      type="text"
                      name="first_name"
                      className="inputtext"
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      value={formik.values.first_name}
                    />
                    {formik.touched.first_name && formik.errors.first_name && (
                      <div className="text-red text-sm">
                        {formik.errors.first_name}
                      </div>
                    )}
                  </div>

                  <div className="text-G300">
                    <label className="block text-sm font-normal mb-2">
                      Store Name
                    </label>
                    <input
                      type="text"
                      name="store_name"
                      className="inputtext"
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      value={formik.values.store_name}
                    />
                    {formik.touched.store_name && formik.errors.store_name && (
                      <div className="text-red text-sm">
                        {formik.errors.store_name}
                      </div>
                    )}
                  </div>
                  <div className="text-G300">
                    <label className="block text-sm font-normal mb-2">
                      Store Logo
                    </label>
                    <div
                      className="flex gap-3 items-center cursor-pointer w-20"
                      onClick={() =>
                        document.getElementById("fileInput").click()
                      }
                    >
                      <img
                        src={previewImage || images.downloadimage}
                        alt="Store Logo Preview"
                        className="w-16 h-16 rounded-full object-fill"
                      />
                      <p>Upload</p>
                    </div>
                    <input
                      id="fileInput"
                      type="file"
                      name="store_logo"
                      accept="image/*"
                      hidden
                      onChange={handleFileChange}
                    />
                  </div>
                  <div className="text-G300">
                    <label className="block text-sm font-normal mb-2">
                      Address
                    </label>
                    <input
                      type="text"
                      name="street_address"
                      className="inputtext"
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      value={formik.values.street_address}
                    />
                    {formik.touched.street_address &&
                      formik.errors.street_address && (
                        <div className="text-red text-sm">
                          {formik.errors.street_address}
                        </div>
                      )}
                  </div>
                  <div className="text-G300">
                    <label className="block text-sm font-normal mb-2">
                      Description
                    </label>
                    <textarea
                      name="description"
                      className="inputtext"
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      value={formik.values.description}
                    />
                    {formik.touched.description &&
                      formik.errors.description && (
                        <div className="text-red text-sm">
                          {formik.errors.description}
                        </div>
                      )}
                  </div>

                  <div className="flex justify-end space-x-4 pt-4">
                    <button
                      type="button"
                      onClick={() => setEditMode(false)}
                      className="w-[93px] text-sm py-2 border border-green text-green rounded-lg"
                    >
                      Close
                    </button>
                    <button
                      type="submit"
                      className={`w-[93px] text-sm py-2 rounded-lg ${submit
                        ? "bg-green50 cursor-not-allowed"
                        : "bg-green text-white"
                        }`}
                      disabled={submit}
                    >
                      {submit ? "Updating..." : "Update"}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}

          <div className="flex flex-col w-full">
            <div className="px-6 flex justify-start items-start gap-5 md:gap-14 border-b border-G30 text-green text-xs sm:text-sm font-medium">
              <button
                onClick={() => setActiveTab("Overview")}
                className={`${activeTab === "Overview"
                  ? "border-b-2 border-green pb-4 inline-block"
                  : ""
                  }`}
              >
                Overview
              </button>
              <button
                onClick={() => setActiveTab("Security")}
                className={`${activeTab === "Security"
                  ? "border-b-2 border-green pb-4 inline-block"
                  : ""
                  }`}
              >
                Security
              </button>
              {/* <button
                onClick={() => setActiveTab("BillingPlans")}
                className={`${activeTab === "BillingPlans"
                  ? "border-b-2 border-green pb-4 inline-block"
                  : ""
                  }`}
              >
                Billing & Plans
              </button> */}
              {/* <button
                onClick={() => setActiveTab("SubscriptionPlan")}
                className={`${activeTab === "SubscriptionPlan"
                  ? "border-b-2 border-green pb-4 inline-block"
                  : ""
                  }`}
              >
                Subscription Plan
              </button> */}
            </div>
          </div>
        </div>

        <div className="mt-3 flex flex-col">{renderContent()}</div>
      </div>
    </div>
  );
};

export default AccountSetting;
